import { ScrollTrigger } from 'gsap/dist/ScrollTrigger';
import {
  getPerformanceLabLenis,
  subscribePerformanceLabLenisReady,
} from 'utils/performanceLabLenisRegistry';

export type ScrollTriggerSetupResult = {
  cleanup?: () => void;
};

export type RunScrollTriggerSetupOptions = {
  isReady: () => boolean;
  setup: () => ScrollTriggerSetupResult | void;
  /** Wait for Lenis scrollerProxy before running setup (default true). */
  waitForLenis?: boolean;
  /** Max ms to wait for Lenis before falling back to native scroll (default 3000). */
  lenisWaitMs?: number;
  /** Max rAF attempts while waiting for isReady (default 120 ≈ 2s). */
  maxAttempts?: number;
  /** Label for dev warnings when maxAttempts is exceeded. */
  debugLabel?: string;
};

const DEFAULT_MAX_ATTEMPTS = 120;
const DEFAULT_LENIS_WAIT_MS = 3000;
const LENIS_POLL_MS = 100;

export function attachScrollTriggerRefreshListeners(): () => void {
  let refreshRafId = 0;
  const requestRefresh = () => {
    if (refreshRafId) return;
    refreshRafId = window.requestAnimationFrame(() => {
      refreshRafId = 0;
      ScrollTrigger.refresh();
    });
  };

  window.addEventListener('resize', requestRefresh);
  window.addEventListener('orientationchange', requestRefresh);

  return () => {
    window.removeEventListener('resize', requestRefresh);
    window.removeEventListener('orientationchange', requestRefresh);
    if (refreshRafId) window.cancelAnimationFrame(refreshRafId);
  };
}

export function whenPerformanceLabLenisReady(onReady: () => void): () => void {
  const unsubscribe = subscribePerformanceLabLenisReady(onReady);

  let pollId = 0;
  if (!getPerformanceLabLenis()) {
    pollId = window.setInterval(() => {
      if (!getPerformanceLabLenis()) return;
      window.clearInterval(pollId);
      pollId = 0;
      onReady();
    }, LENIS_POLL_MS);
  }

  return () => {
    unsubscribe();
    if (pollId) window.clearInterval(pollId);
  };
}

export function schedulePerformanceLabScrollTriggerRefresh(): () => void {
  let rafId = 0;
  rafId = window.requestAnimationFrame(() => {
    rafId = window.requestAnimationFrame(() => {
      rafId = 0;
      ScrollTrigger.refresh();
    });
  });

  return () => {
    if (rafId) window.cancelAnimationFrame(rafId);
  };
}

/**
 * Poll until DOM is ready, optionally wait for Lenis, then run ScrollTrigger setup.
 * Returns a unified cleanup for effect teardown.
 */
export function runScrollTriggerSetup({
  isReady,
  setup,
  waitForLenis = true,
  lenisWaitMs = DEFAULT_LENIS_WAIT_MS,
  maxAttempts = DEFAULT_MAX_ATTEMPTS,
  debugLabel,
}: RunScrollTriggerSetupOptions): () => void {
  let cancelled = false;
  let setupStarted = false;
  let setupRafId = 0;
  let lenisWaitTimeoutId = 0;
  let cleanupRefresh: (() => void) | null = null;
  let cleanupLenis: (() => void) | null = null;
  let setupCleanup: (() => void) | null = null;
  let attempts = 0;

  const clearLenisWait = () => {
    if (lenisWaitTimeoutId) {
      window.clearTimeout(lenisWaitTimeoutId);
      lenisWaitTimeoutId = 0;
    }
    cleanupLenis?.();
    cleanupLenis = null;
  };

  const runSetup = () => {
    if (cancelled) return;

    const result = setup();
    if (result?.cleanup) {
      setupCleanup = result.cleanup;
    }

    cleanupRefresh = attachScrollTriggerRefreshListeners();
    ScrollTrigger.refresh();
  };

  const runSetupOnce = () => {
    if (cancelled || setupStarted) return;
    setupStarted = true;

    if (lenisWaitTimeoutId) {
      window.clearTimeout(lenisWaitTimeoutId);
      lenisWaitTimeoutId = 0;
    }

    runSetup();
  };

  const beginSetup = () => {
    if (cancelled) return;

    if (waitForLenis && !getPerformanceLabLenis()) {
      cleanupLenis = whenPerformanceLabLenisReady(() => {
        if (setupStarted) {
          ScrollTrigger.refresh();
          return;
        }
        runSetupOnce();
      });

      lenisWaitTimeoutId = window.setTimeout(() => {
        lenisWaitTimeoutId = 0;
        if (process.env.NODE_ENV === 'development' && debugLabel) {
          console.warn(
            `[performanceLabScrollTrigger] Lenis not ready after ${lenisWaitMs}ms, falling back to native scroll: ${debugLabel}`,
          );
        }
        runSetupOnce();
      }, lenisWaitMs);

      return;
    }

    runSetupOnce();
  };

  const pollReady = () => {
    if (cancelled) return;

    if (isReady()) {
      beginSetup();
      return;
    }

    attempts += 1;
    if (attempts >= maxAttempts) {
      if (process.env.NODE_ENV === 'development' && debugLabel) {
        console.warn(
          `[performanceLabScrollTrigger] Setup timed out waiting for readiness: ${debugLabel}`,
        );
      }
      return;
    }

    setupRafId = window.requestAnimationFrame(pollReady);
  };

  pollReady();

  return () => {
    cancelled = true;
    if (setupRafId) window.cancelAnimationFrame(setupRafId);
    clearLenisWait();
    cleanupRefresh?.();
    setupCleanup?.();
  };
}
